const graphSettings = {
  wrapperHeight: 432,
  wrapperHeightSticky: 280,
};

/**
 * Slider bounds: symmetric around `graphSettings.wrapperHeight` so the default sits at
 * the track midpoint. (min + max must equal 2 × default; with min = default/2, max is 1.5× default.)
 */
export const STACKED_REPORT_GRAPH_HEIGHT = {
  min: graphSettings.wrapperHeight / 2,
  max: graphSettings.wrapperHeight * 2 - graphSettings.wrapperHeight / 2,
  step: 8,
};

/**
 * Clamp main graph height and derive sticky height using the default layout ratio.
 */
export function getStackedGraphLayoutHeights(mainHeightPx) {
  const baseMain = graphSettings.wrapperHeight;
  const ratio = graphSettings.wrapperHeightSticky / graphSettings.wrapperHeight;
  let main = Number(mainHeightPx);
  if (!Number.isFinite(main)) main = baseMain;
  main = Math.round(main);
  main = Math.min(STACKED_REPORT_GRAPH_HEIGHT.max, Math.max(STACKED_REPORT_GRAPH_HEIGHT.min, main));
  const sticky = Math.max(100, Math.round(main * ratio));
  return {wrapperHeight: main, wrapperHeightSticky: sticky};
}

/** Realtime auto-refresh: dropdown value is seconds; labels are user-facing only. */
export const REALTIME_REFRESH_INTERVAL_OPTIONS = [
  {value: 15, label: '15 seconds'},
  {value: 30, label: '30 seconds'},
  {value: 60, label: '1 minute'},
  {value: 120, label: '2 minutes'},
  {value: 300, label: '5 minutes'},
];

export function getRealtimeRefreshIntervalSecondsOrDefault(stored) {
  const allowed = REALTIME_REFRESH_INTERVAL_OPTIONS.map(o => o.value);
  return allowed.includes(stored) ? stored : 15;
}

const sharedIndicatorLineBase = {
  right: 30,
  width: 15,
  display: 'inline-block',
  position: 'absolute',
  top: 25,
};

const sharedIndicatorStyles = {
  // Used in metricsConfig.*.indicatorStyle blocks
  line: {
    height: 4,
    ...sharedIndicatorLineBase,
  },
  dashedLine: {
    height: 1,
    ...sharedIndicatorLineBase,
  },
  column: {
    right: 30,
    height: 36,
    width: 15,
    display: 'inline-block',
    marginLeft: 10,
    borderRadius: 2,
    top: 18,
    position: 'absolute',
  },
};

// Configuration used to describe dynamic data in that is displayed in the graph
// Not all the options is always present in the data, so we need to check if the data is present
// Key is the name of the data in the API response
const {getSSPLogo} = require('../../../helpers/getSSPLogo');
const countriesList = require('../../../misc/countries.json');
const {findFlagUrlByIso2Code} = require('country-flags-svg');

// Flat UI Colors - Mixed from British, Spanish, and Australian palettes
// (excluding blues similar to Google's #3498db)
const flatUIColors = [
  '#e74c3c', // Alizarin (red)
  '#2ecc71', // Emerald (green)
  '#9b59b6', // Amethyst (purple)
  '#f39c12', // Orange
  '#1abc9c', // Turquoise
  '#e67e22', // Carrot (orange)
  '#16a085', // Green Sea
  '#8e44ad', // Wisteria (dark purple)
  '#f1c40f', // Sun Flower (yellow)
  '#27ae60', // Nephritis (green)
  '#c0392b', // Pomegranate (dark red)
  '#d35400', // Pumpkin (dark orange)
  '#e91e63', // Radical Red
  '#ff5722', // Deep Orange
  '#00b894', // Mint Leaf
  '#fdcb6e', // Warm Flame
  '#6c5ce7', // Exodus Fruit
  '#fd79a8', // Pink Glamour
  '#a29bfe', // Light Slate Blue
  '#00cec9', // Robin's Egg Blue
];

// Better hash function (FNV-1a) to minimize collisions between source names
const hashSourceName = name => {
  let hash = 2166136261; // FNV offset basis
  for (let i = 0; i < name.length; i++) {
    hash ^= name.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return Math.abs(hash >>> 0); // Convert to unsigned 32-bit integer
};

const getColorFromPalette = sourceName => {
  // Remove 'S2S' suffix so variants get the same color as their base source
  // e.g., "Amazon" and "AmazonS2S" will both hash to the same color
  const normalizedName = sourceName.replace(/s2s$/i, '');

  // Hash the normalized name to get a consistent color index
  const hash = hashSourceName(normalizedName);
  return flatUIColors[hash % flatUIColors.length];
};

// Prebid sources config is different from demand sources config, because in prebid we have to generate the sources on the fly
const prebidSourcesConfig = (theme, data, attributeGroups) => {
  // Sources is dynamic for prebid stack, so we have to generate it on the fly and then match what we know
  const sourcesConfig = {};
  const googleColor = theme.palette.graphColors['adx'];

  data?.forEach(row => {
    Object.keys(row)
      .filter(key => key !== 'date')
      .forEach(key => {
        let source = key.split(' ')[0];
        if (!sourcesConfig[source]) {
          sourcesConfig[source] = {
            logo: getSSPLogo(source, attributeGroups),
          };
        }
      });
  });

  // Add the color to the sourcesConfig
  const sources = Object.keys(sourcesConfig);

  // Assign Google its fixed color
  if (sourcesConfig['Google']) {
    sourcesConfig['Google'].color = googleColor;
  }

  // Assign colors to all other sources
  const otherSources = sources.filter(key => key !== 'Google');

  otherSources.forEach(key => {
    sourcesConfig[key].color = getColorFromPalette(key);
  });

  return sourcesConfig;
};

const deviceSourcesConfig = theme => ({
  0: {color: theme.palette.graphColors.adx, displayNameOverride: 'Desktop'},
  1: {color: theme.palette.graphColors.revenue, displayNameOverride: 'Tablet'},
  2: {color: theme.palette.graphColors.pvRPM, displayNameOverride: 'Mobile'},
});

const countrySourcesConfig = (theme, data = []) => {
  const config = {};

  // Add any countries that appear in data but are missing from the list
  data.forEach(row => {
    Object.keys(row)
      .filter(key => key !== 'date')
      .forEach(key => {
        const source = key.split(' ')[0];
        if (!config[source]) {
          config[source] = {
            color: getColorFromPalette(source),
            displayNameOverride: countriesList.find(c => c.id === source.toUpperCase())?.text,
            logo: findFlagUrlByIso2Code(source),
          };
        }
      });
  });

  // Special override for other countries
  config['other'] = {
    color: theme.palette.graphColors['themeLightGray'],
    displayNameOverride: 'Other Countries combined',
    tooltip:
      'Countries dimension returns top 30 countries by impression volume. The rest of the countries are combined under this category.',
  };

  return config;
};

// Demand sources config
const demandSourcesConfig = theme => ({
  AdX: {
    logo: 'https://www.gstatic.com/admanager/ad_manager.ico',
    color: theme.palette.graphColors['adx'],
  },
  AdSense: {
    logo: 'https://storage.googleapis.com/support-kms-prod/bJppRS0Z8FmVujFmM7KFHADzbq3L0PPb1Wpj',
    color: theme.palette.graphColors['adsense'],
  },
  'Prebid Direct': {
    logo: 'https://prebid.org/wp-content/uploads/2023/06/cropped-prebid-logo-180x180.png',
    color: theme.palette.graphColors['amazon'],
    direct: true,
    displayNameOverride: 'Prebid',
  },
  'Prebid Reseller': {
    logo: 'https://prebid.org/wp-content/uploads/2023/06/cropped-prebid-logo-180x180.png',
    color: theme.palette.graphColors['prebid'],
    displayNameOverride: 'Prebid',
  },
  Amazon: {
    logo: 'https://www.google.com/s2/favicons?domain=https://advertising.amazon.com/favicon.ico',
    color: theme.palette.graphColors['amazon'],
  },
  'Open Bidding': {
    logo: 'https://www.google.com/favicon.ico',
    color: theme.palette.graphColors['uRPM'],
  },
  Other: {
    color: theme.palette.graphColors['themeLightGray'],
    tooltip:
      '“Includes the remaining GAM priorities not covered in this report. A complete list can be found in the Direct Stack report.',
    logo: 'https://www.gstatic.com/admanager/ad_manager.ico',
  },
  'Programmatic Direct': {
    color: theme.palette.graphColors['CTR'],
    tooltip: 'Programmatic Guaranteed and Preferred Deals in GAM',
    logo: 'https://www.gstatic.com/admanager/ad_manager.ico',
  },
  'Price Priority': {
    color: theme.palette.graphColors['eCPM'],
    logo: 'https://www.gstatic.com/admanager/ad_manager.ico',
    tooltip: `Price priority is served primarily based on price, with optional daily or lifetime delivery caps. Used to fill sites unsold inventory with the highest-paying line item available.`,
  },
});

// Demand sources config
// Tooltips taken from: https://support.google.com/admanager/answer/177279?hl=en
const directSourcesConfig = theme => ({
  sponsorship: {
    tooltip: `Sponsorship is served based upon a defined percentage of impressions and a start and end time. This priority is used for directly sold campaigns when buyer wants to "takeover" a page or site. Can also be used to evenly rotate line items regardless of impression volume.`,
    // logo: 'https://www.gstatic.com/admanager/ad_manager.ico',
    color: theme.palette.graphColors['adx'],
  },
  standard: {
    tooltip: `Standard is served based upon a defined impression goal and start and end time. This priority is used for directly sold campaigns when your buyer wants a specific number of impression to serve. Delivery pacing is adjusted to meet the defined goal.`,
    color: theme.palette.graphColors['revenue'],
  },
  network: {
    tooltip: `Network is served based upon a defined percentage of impressions. This priority is used for your partner ad networks which don't have an impression goal.`,
    color: theme.palette.graphColors['prebid'],
  },
  bulk: {
    tooltip: `Bulk is served based upon a defined impression goal, but because the inventory for bulk line items isn't guaranteed, the impression goal functions more like an impression cap, limiting the number of impressions that can be delivered. Used for partners who have ordered a maximum number of impressions but aren't concerned about delivery timelines or guarantees.`,
    color: theme.palette.graphColors['amazon'],
  },
  price_priority: {
    tooltip: `Price priority is served primarily based on price, with optional daily or lifetime delivery caps. Used to fill sites unsold inventory with the highest-paying line item available.`,
    color: theme.palette.graphColors['eCPM'],
  },
  house: {
    tooltip: `House priority is only served when no remnant line items (Network, Bulk, Price Priority), Ad Exchange or Open Bidding demand are available to serve`,
    color: theme.palette.graphColors['themeLightGray'],
  },
  adsense: {
    tooltip: `Specific inventory available to AdSense buyers via a line item.`,
    color: theme.palette.graphColors['CTR'],
  },
  ad_exchange: {
    tooltip: `Specific inventory available to Authorized Buyers and the Open Auction.`,
    color: theme.palette.graphColors['CTR'],
  },
  preferred_deal: {
    tooltip: `The Preferred Deals is a fixed priority value that ensures it wins ahead of the Open Auction and all other line item types except Sponsorship and Standard line items. Sponsorship and Standard line items generally serve ahead of Preferred Deals except when Dynamic Allocation is activated and non-guaranteed competes with guaranteed line items in real time.`,
    color: theme.palette.graphColors['CTR'],
  },
  bumper: {
    color: theme.palette.graphColors['CTR'],
  },
  admob: {
    color: theme.palette.graphColors['CTR'],
  },
  unknown: {
    color: theme.palette.graphColors['CTR'],
  },
});

// Metrics that are used in the graph
const createMetricsConfig = (theme, hideSeparateGraphs, reportType = 'stacked') => {
  // Base config - use existing config for all stacked report types (prebid, demand, direct)
  const baseConfig = {
    fixed: {
      requests: {
        dataField: 'All Imp',
      },
      programmaticEligibleRequests: {
        dataField: 'Programmatic Eligible Requests',
      },
      date: {
        dataField: 'date',
      },
    },
    perSource: {
      impressions: {
        dataField: 'Paid Imp',
        displayName: 'Impressions',
        comparisonWithTotalEnabled: true,
        cumulative: true,
        hideIndicator: false,
        graphType: 'column',
        graphValueAxisConfig: {
          stackType: 'regular',
          position: 'right',
          id: 'impressions',
          title: 'Impressions',
        },
        indicatorType: 'column',
        indicatorStyle: {
          border: `1px solid ${theme.palette.graphColors.historicalBackground}`,
          ...sharedIndicatorStyles.column,
        },
      },
      revenue: {
        dataField: 'Rev',
        displayName: 'Revenue',
        symbol: '€',
        comparisonWithTotalEnabled: true,
        cumulative: true,
        hideIndicator: false,
        fillArea: true,
        graphType: 'smoothedLine',
        graphValueAxisConfig: {
          stackType: 'regular',
          position: 'right',
          id: 'revenue',
          title: 'Revenue',
        },
        indicatorType: 'line',
        indicatorStyle: {
          ...sharedIndicatorStyles.line,
        },
      },
      eCPM: {
        dataField: 'eCPM',
        displayName: 'eCPM',
        symbol: '€',
        comparisonWithTotalEnabled: false,
        cumulative: false,
        hideIndicator: hideSeparateGraphs,
        graphType: 'smoothedLine',
        graphValueAxisConfig: {
          id: 'eCPM',
          title: 'eCPM',
          position: 'right',
        },
        indicatorType: 'dashed-line',
        indicatorStyle: {
          ...sharedIndicatorStyles.dashedLine,
        },
      },
      rCPM: {
        tooltip:
          'rCPM or Ad Request CPM is the average price per 1000 programmatic eligible ad requests. Programmatic eligible ad requests include ad requests to all programmatic sources listed on the Demand Stack but do not include ad requests to direct campaigns like sponsorships, standard campaigns etc.\n\nrCPM = (Total Revenue ÷ Programmatic Eligible Ad Requests) × 1,000',
        dataField: 'rCPM',
        displayName: 'rCPM',
        symbol: '€',
        comparisonWithTotalEnabled: false,
        cumulative: false,
        hideIndicator: hideSeparateGraphs,
        graphType: 'smoothedLine',
        graphValueAxisConfig: {
          id: 'rCPM',
          title: 'rCPM',
          position: 'right',
        },
        indicatorType: 'dashed-line',
        indicatorStyle: {
          ...sharedIndicatorStyles.dashedLine,
        },
      },
    },
    calculated: {
      eCPM: {
        formula: 'Rev / Paid Imp * 1000',
        displayName: 'eCPM',
        dataField: 'eCPM',
        comparisonWithTotalEnabled: false,
        cumulative: false,
        graphType: 'smoothedLine',
        graphValueAxisConfig: {
          id: 'eCPM',
          title: 'eCPM',
          position: 'right',
        },
        indicatorType: 'dashed-line',
        indicatorStyle: {
          ...sharedIndicatorStyles.dashedLine,
        },
      },
      rCPM: {
        formula: 'Rev / Programmatic Eligible Requests * 1000',
        displayName: 'rCPM',
        dataField: 'rCPM',
        comparisonWithTotalEnabled: false,
        cumulative: false,
        graphType: 'smoothedLine',
        graphValueAxisConfig: {
          id: 'rCPM',
          title: 'rCPM',
          position: 'right',
        },
        indicatorType: 'dashed-line',
        indicatorStyle: {
          ...sharedIndicatorStyles.dashedLine,
        },
      },
    },
  };

  // Override only for analytics
  if (reportType === 'analytics') {
    return {
      fixed: {
        date: {
          dataField: 'date',
        },
      },
      perSource: {
        impressions: {
          tooltip:
            'Total number of ad impressions served (winning bids that resulted in displayed ads).\n\nFormula: sum(bid_won)',
          dataField: 'TotalImpressions',
          displayName: 'Impressions',
          comparisonWithTotalEnabled: true,
          cumulative: true,
          hideIndicator: false,
          graphType: 'column',
          graphValueAxisConfig: {
            stackType: 'regular',
            position: 'right',
            id: 'impressions',
            title: 'Impressions',
          },
          indicatorType: 'column',
          indicatorStyle: {
            border: `1px solid ${theme.palette.graphColors.historicalBackground}`,
            ...sharedIndicatorStyles.column,
          },
        },
        winRate: {
          dataField: 'WinRate',
          displayName: 'Win Rate',
          tooltip:
            'Percentage of ad requests that resulted in a winning bid and ad impression. Calculated as wins divided by total requests.\n\nFormula: (sum(bid_won) / sum(bid_requested)) × 100',
          comparisonWithTotalEnabled: true,
          cumulative: false,
          symbol: '%',
          // Weight averages by impressions to reflect volume
          averageWeightField: 'TotalImpressions',
          hideIndicator: false,
          graphType: 'smoothedLine',
          graphValueAxisConfig: {
            id: 'winRate',
            title: 'Win Rate',
            position: 'right',
          },
          indicatorType: 'line',
          indicatorStyle: {
            ...sharedIndicatorStyles.line,
          },
        },
        timeoutRate: {
          dataField: 'TimeoutRate',
          displayName: 'Timeout Rate',
          tooltip:
            'Percentage of ad requests that timed out before receiving a bid response. Lower is better.\n\nFormula: (sum(bid_timeout) / sum(bid_requested)) × 100',
          comparisonWithTotalEnabled: true,
          cumulative: false,
          symbol: '%',
          invertTrend: true,
          // Weight averages by impressions to reflect volume
          averageWeightField: 'TotalImpressions',
          hideIndicator: false,
          graphType: 'smoothedLine',
          graphValueAxisConfig: {
            id: 'timeoutRate',
            title: 'Timeout Rate',
            position: 'right',
          },
          indicatorType: 'line',
          indicatorStyle: {
            ...sharedIndicatorStyles.line,
          },
        },
        bidRate: {
          dataField: 'BidRate',
          displayName: 'Bid Rate',
          tooltip:
            'Percentage of ad requests that received at least one bid response from bidders. Higher indicates better bidder participation.\n\nFormula: (sum(bid_response) / sum(bid_requested)) × 100',
          comparisonWithTotalEnabled: false,
          cumulative: false,
          symbol: '%',
          // Weight averages by impressions to reflect volume
          averageWeightField: 'TotalImpressions',
          hideIndicator: false,
          graphType: 'smoothedLine',
          graphValueAxisConfig: {
            id: 'bidRate',
            title: 'Bid Rate',
            position: 'right',
          },
          indicatorType: 'line',
          indicatorStyle: {
            ...sharedIndicatorStyles.line,
          },
        },
        adRequests: {
          dataField: 'AdRequests',
          displayName: 'Ad Requests',
          tooltip:
            'Total number of ad requests made during the selected time period.\n\nFormula: sum(bid_requested)',
          comparisonWithTotalEnabled: true,
          cumulative: true,
          disableTotals: true,
          hideIndicator: false,
          graphType: 'column',
          graphValueAxisConfig: {
            stackType: 'regular',
            position: 'right',
            id: 'adRequests',
            title: 'Ad Requests',
          },
          indicatorType: 'column',
          indicatorStyle: {
            border: `1px solid ${theme.palette.graphColors.historicalBackground}`,
            ...sharedIndicatorStyles.column,
          },
        },
        revenue: {
          dataField: 'TotalRevenue',
          displayName: 'Revenue',
          tooltip:
            'Total revenue generated from ad impressions, displayed in the selected currency unit.\n\nFormula: sum(revenue) / 1000',
          symbol: '€',
          comparisonWithTotalEnabled: true,
          cumulative: true,
          hideIndicator: false,
          graphType: 'smoothedLine',
          fillArea: true,
          graphValueAxisConfig: {
            stackType: 'regular',
            position: 'right',
            id: 'revenue',
            title: 'TotalRevenue',
          },
          indicatorType: 'line',
          indicatorStyle: {
            ...sharedIndicatorStyles.line,
          },
        },
        avgBidCPM: {
          dataField: 'AvgBidCPM',
          displayName: 'Avg Bid CPM',
          tooltip:
            'Average cost per mille (thousand impressions) of the bids submitted by bidders. Represents the average bid price.\n\nFormula: avg(bid_cpm) where bid_response > 0',
          comparisonWithTotalEnabled: false,
          cumulative: false,
          symbol: '€',
          // Weight averages by impressions to reflect volume
          averageWeightField: 'TotalImpressions',
          hideIndicator: false,
          graphType: 'smoothedLine',
          graphValueAxisConfig: {
            id: 'avgBidCPM',
            title: 'Avg Bid CPM',
            position: 'right',
          },
          indicatorType: 'line',
          indicatorStyle: {
            height: 4,
            width: 15,
            display: 'inline-block',
            position: 'absolute',
            top: 32,
          },
        },
        eCPM: {
          dataField: 'eCPM',
          displayName: 'eCPM',
          tooltip: 'Effective cost per thousand impressions.',
          comparisonWithTotalEnabled: false,
          cumulative: false,
          symbol: '€',
          // Weight averages by impressions to reflect volume
          averageWeightField: 'TotalImpressions',
          hideIndicator: false,
          graphType: 'smoothedLine',
          graphValueAxisConfig: {
            id: 'eCPM',
            title: 'eCPM',
            position: 'right',
          },
          indicatorType: 'line',
          indicatorStyle: {
            height: 4,
            width: 15,
            display: 'inline-block',
            position: 'absolute',
            top: 32,
          },
        },
        Latency: {
          dataField: 'AVGlatency',
          displayName: 'Avg Latency',
          tooltip:
            'Average response time in milliseconds for bid requests that received responses. Measures bidder performance speed.\n\nFormula: avg(latency_ms) where bid_response > 0',
          comparisonWithTotalEnabled: false,
          cumulative: false,
          symbol: 'ms',
          invertTrend: true,
          disableMillify: true,
          // Weight latency averages by impressions so high-volume rows influence the mean
          averageWeightField: 'TotalImpressions',
          hideIndicator: false,
          graphType: 'smoothedLine',
          graphValueAxisConfig: {
            id: 'AVGlatency',
            title: 'Avg Latency',
            position: 'right',
          },
          indicatorType: 'line',
          indicatorStyle: {
            height: 4,
            width: 15,
            display: 'inline-block',
            position: 'absolute',
            top: 32,
          },
        },
      },
      calculated: {},
    };
  }

  // Return base config for all other types (prebid, demand, direct)
  if (reportType !== 'demand') {
    const perSource = {...baseConfig.perSource};
    const calculated = {...baseConfig.calculated};
    delete perSource.rCPM;
    delete calculated.rCPM;
    return {...baseConfig, perSource, calculated};
  }

  // Demand: expose day-level Programmatic Eligible Requests as "Ad Requests"
  // (not all accounts receive this field from the reporting API).
  return {
    ...baseConfig,
    perSource: {
      ...baseConfig.perSource,
      adRequests: {
        dataField: 'Programmatic Eligible Requests',
        displayName: 'Ad Requests',
        tooltip:
          'Programmatic eligible ad requests for the selected period. Includes requests to programmatic demand sources on the Demand Stack; excludes direct campaigns such as sponsorships and standard campaigns.',
        comparisonWithTotalEnabled: false,
        cumulative: true,
        // Value exists only at day level (same for every demand source; do not sum across sources).
        dayLevelOnly: true,
        hideIndicator: false,
        graphType: 'column',
        graphValueAxisConfig: {
          stackType: 'regular',
          position: 'right',
          id: 'adRequests',
          title: 'Ad Requests',
        },
        indicatorType: 'column',
        indicatorStyle: {
          border: `1px solid ${theme.palette.graphColors.historicalBackground}`,
          ...sharedIndicatorStyles.column,
        },
      },
    },
  };
};

const balloonConfig = {
  adjustBorderColor: false,
  borderColor: '#e75d3c000',
  fillColor: '#e75d3c000',
  fillAlpha: 0,
  shadowAlpha: 0,
  color: '#fff',
  cornerRadius: 4,
  maxWidth: 400,
  disableMouseEvents: false,
  fixedPosition: false,
  showBullet: true,
  pointerOrientation: 'vertical',
  pointerWidth: 0,
  pointerHeight: 0,
};

const getBalloonDateFormat = period => {
  switch (period?.label) {
    case 'realtime':
      return 'YYYY MMMM D, HH:mm:ss';
    case 'Last 24 hours':
    case 'Today':
      return 'YYYY MMMM D, HH:mm';
    default:
      return 'YYYY MMMM D';
  }
};

export {
  prebidSourcesConfig,
  deviceSourcesConfig,
  countrySourcesConfig,
  demandSourcesConfig,
  directSourcesConfig,
  createMetricsConfig,
  balloonConfig,
  graphSettings,
  getBalloonDateFormat,
};
